import { useEffect, useRef } from "react";
import "./index.css";

function useFadeIn() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("visible");
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

function FadeIn({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  const ref = useFadeIn();
  return <div ref={ref} className={`fade-in ${className}`}>{children}</div>;
}

/* ============ NAV ============ */
function Nav() {
  return (
    <nav className="nav">
      <a href="#" className="nav-logo">
        <div className="nav-logo-icon">AH</div>
        Appraiser Helper
      </a>
      <ul className="nav-links">
        <li><a href="#features">Features</a></li>
        <li><a href="#how-it-works">How It Works</a></li>
        <li><a className="nav-cta" href="#cta">Get Started</a></li>
      </ul>
      <button className="nav-mobile-toggle" aria-label="Menu">&#9776;</button>
    </nav>
  );
}

/* ============ HERO ============ */
function Hero() {
  return (
    <section className="hero">
      <div className="hero-badge">
        <span className="hero-badge-dot" />
        AI-Powered Appraisal Operations
      </div>
      <h1>
        Run your appraisal shop<br />
        <span className="highlight">at peak efficiency.</span>
      </h1>
      <p>
        Automated billing, AI-powered review, and workflow tools built
        for appraisal firms — so your team spends less time on admin
        and more time on valuations.
      </p>
      <div className="hero-buttons">
        <button className="btn-primary">Request Early Access &rarr;</button>
        <button className="btn-secondary">Watch Demo</button>
      </div>
    </section>
  );
}

/* ============ DEMO ============ */
function HeroDemo() {
  return (
    <div className="hero-demo">
      <div className="demo-window">
        <div className="demo-titlebar">
          <span className="demo-dot red" />
          <span className="demo-dot yellow" />
          <span className="demo-dot green" />
        </div>
        <div className="demo-content">
          <div className="demo-message">
            <div className="demo-avatar user">You</div>
            <div className="demo-bubble">
              Review the Johnson report — check comps, adjustments, and USPAP compliance before I send it out.
            </div>
          </div>
          <div className="demo-message">
            <div className="demo-avatar ai">AH</div>
            <div className="demo-bubble">
              Reviewed <strong>Johnson, 742 Elm St</strong>. Found <strong>2 issues</strong>:<br />
              <span className="metric">Comp #2: GLA adj. exceeds 25%</span>
              <span className="metric">Missing market conditions comment</span><br />
              Everything else checks out — USPAP scope of work, neighborhood analysis, and reconciliation are clean. Want me to draft fixes?
            </div>
          </div>
          <div className="demo-message">
            <div className="demo-avatar user">You</div>
            <div className="demo-bubble">
              Yes, fix both. Also, generate the invoice for this order — it's a conventional 1004, standard fee.
            </div>
          </div>
          <div className="demo-message">
            <div className="demo-avatar ai">AH</div>
            <div className="demo-bubble">
              Done. Comp #2 adjustment corrected and market conditions comment added. Invoice <strong>#INV-2026-0438</strong> created:<br />
              <span className="metric">1004 Appraisal: $500</span>
              <span className="metric">Status: Sent to client</span><br />
              Report is ready for delivery. Want me to queue it for submission?
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


/* ============ FEATURES ============ */
function Features() {
  return (
    <section className="features" id="features">
      <FadeIn>
        <div className="section-label">Features</div>
        <h2 className="section-title">Everything Your Appraisal Firm Needs</h2>
        <p className="section-subtitle">
          Billing, quality control, and operational efficiency — all in one
          platform built for appraisal shops of every size.
        </p>
      </FadeIn>

      <div className="features-grid">
        <FadeIn className="feature-card green">
          <div className="feature-card-label">Automated Billing</div>
          <h3>Invoicing on Autopilot</h3>
          <p>
            Generate invoices automatically when orders are completed. Track
            payments, manage fee schedules by product type, and sync with
            your accounting — no more chasing down checks or updating spreadsheets.
          </p>
          <div className="feature-card-visual">
            <div className="feature-stat">
              <span className="feature-stat-value">Auto</span>
              <span className="feature-stat-label">Invoice generation</span>
            </div>
            <div className="feature-stat">
              <span className="feature-stat-value">Real-time</span>
              <span className="feature-stat-label">Payment tracking</span>
            </div>
            <div className="feature-stat">
              <span className="feature-stat-value">Custom</span>
              <span className="feature-stat-label">Fee schedules</span>
            </div>
          </div>
        </FadeIn>

        <FadeIn className="feature-card blue">
          <div className="feature-card-label">AI Review</div>
          <h3>Automated Quality Control</h3>
          <p>
            AI reviews every report before it leaves your office — checking
            comps, adjustments, USPAP compliance, and lender-specific
            requirements. Catch errors before your clients do.
          </p>
        </FadeIn>

        <FadeIn className="feature-card purple">
          <div className="feature-card-label">Order Management</div>
          <h3>Track Every Assignment</h3>
          <p>
            Assign orders, monitor deadlines, and see where every report
            stands across your entire team. No more status update emails
            or lost orders.
          </p>
        </FadeIn>

        <FadeIn className="feature-card amber">
          <div className="feature-card-label">In-House Efficiency</div>
          <h3>Streamline Your Operations</h3>
          <p>
            From order intake to delivery, eliminate manual steps with
            automated workflows. Appraiser scheduling, workload balancing,
            and turn-time tracking — all in one dashboard.
          </p>
          <div className="feature-card-visual">
            <div className="feature-stat">
              <span className="feature-stat-value">Orders</span>
              <span className="feature-stat-label">Pipeline view</span>
            </div>
            <div className="feature-stat">
              <span className="feature-stat-value">Teams</span>
              <span className="feature-stat-label">Workload balancing</span>
            </div>
            <div className="feature-stat">
              <span className="feature-stat-value">SLAs</span>
              <span className="feature-stat-label">Turn-time tracking</span>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

/* ============ HOW IT WORKS ============ */
function HowItWorks() {
  return (
    <section className="how-it-works" id="how-it-works">
      <div style={{ maxWidth: 1000, margin: "0 auto" }}>
        <FadeIn>
          <div className="section-label">How It Works</div>
          <h2 className="section-title">Up and running in minutes</h2>
          <p className="section-subtitle">
            No complicated setup. Plug in your existing workflow and
            let Appraiser Helper handle the rest.
          </p>
        </FadeIn>

        <FadeIn>
          <div className="steps">
            <div className="step">
              <div className="step-number">1</div>
              <h3>Import Your Orders</h3>
              <p>
                Connect your AMC feeds, email intake, or add orders
                manually. Appraiser Helper organizes everything into
                a single pipeline with automated assignment.
              </p>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <h3>AI Reviews & QC</h3>
              <p>
                When a report is complete, our AI runs a full review —
                checking comps, adjustments, compliance, and
                lender overlays — and flags issues instantly.
              </p>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <h3>Bill & Deliver</h3>
              <p>
                Invoices are generated automatically. Deliver the report,
                track payment status, and move on to the next order — all
                from one screen.
              </p>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

/* ============ METRICS ============ */
function Metrics() {
  return (
    <section className="metrics">
      <FadeIn>
        <div className="metrics-inner">
          <div className="metric-item">
            <h3>70%</h3>
            <p>Less time on admin</p>
          </div>
          <div className="metric-item">
            <h3>Zero</h3>
            <p>Manual invoices</p>
          </div>
          <div className="metric-item">
            <h3>AI</h3>
            <p>Review on every report</p>
          </div>
          <div className="metric-item">
            <h3>1 dashboard</h3>
            <p>For your whole operation</p>
          </div>
        </div>
      </FadeIn>
    </section>
  );
}


/* ============ CTA ============ */
function CTA() {
  return (
    <section className="cta" id="cta">
      <FadeIn>
        <h2>Ready to run a more efficient shop?</h2>
        <p>
          Automated billing, AI-powered reviews, and a single dashboard
          for your entire appraisal operation. Get early access today.
        </p>
        <button className="btn-primary">Request Early Access &rarr;</button>
      </FadeIn>
    </section>
  );
}


/* ============ APP ============ */
export function App() {
  return (
    <>
      <Nav />
      <Hero />
      <HeroDemo />
      <Features />
      <Metrics />
      <HowItWorks />
      <CTA />
    </>
  );
}

export default App;
